<script setup lang="ts">
import type { SlotCapacityInfo } from '@/lib/types/slotCapacity';
import type { ReservationResourceCustomField } from '@/lib/types/reservation';
import type { Resource } from '@/lib/types/resource';
import { useResourcesStore } from '@/store/resource';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ResourceSlotCard from './ResourceSlotCard.vue';

const { t } = useI18n();
const resourcesStore = useResourcesStore();

export interface ResourceSlotConfig {
    startTime: string | null;
    endTime: string | null;
    customFields: ReservationResourceCustomField[];
}

interface Props {
    selectedDate: Date | null;
    selectedResourceIds: string[];
    resources: Resource[];
    resourcesLoading: boolean;
    resourceSlotConfigs: Map<string, ResourceSlotConfig>;
    slotCapacities: Map<string, SlotCapacityInfo>;
    attendees: number;
    editingReservationId: string | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    'update:selectedResourceIds': [ids: string[]];
    'update:resourceSlotConfig': [resourceId: string, config: ResourceSlotConfig];
    'remove-resource': [resourceId: string];
    'update:selectedDate': [date: Date | null];
    'slot-validation-errors-change': [hasErrors: boolean];
}>();

// Track per-resource validation errors from ResourceSlotCard
const resourceValidationErrors = reactive(new Map<string, boolean>());

const hasAnySlotValidationError = computed(() => {
    for (const [resourceId, hasError] of resourceValidationErrors) {
        if (props.selectedResourceIds.includes(resourceId) && hasError) return true;
    }
    return false;
});

watch(hasAnySlotValidationError, (val) => {
    emit('slot-validation-errors-change', val);
}, { immediate: true });

function handleSlotValidationChange(resourceId: string, hasError: boolean) {
    resourceValidationErrors.set(resourceId, hasError);
}

// Date v-model bridge
const dateModel = computed({
    get: () => props.selectedDate,
    set: (val: Date | null) => emit('update:selectedDate', val)
});

// Date range for date picker
const today = new Date();
const minDate = new Date(today);
minDate.setFullYear(today.getFullYear() - 1);
const maxDate = new Date(today);
maxDate.setFullYear(today.getFullYear() + 1);

// Active resource tracking
const activeResourceId = ref<string | null>(null);
const mobileDialogVisible = ref(false);

function handleResourceClick(resourceId: string) {
    activeResourceId.value = resourceId;
    if (window.innerWidth < 1024) {
        mobileDialogVisible.value = true;
    }
}

const activeResource = computed(() => {
    if (!activeResourceId.value) return null;
    return props.resources.find(r => r.id === activeResourceId.value) || null;
});

// Selected resources as Resource objects
const selectedResources = computed(() =>
    props.selectedResourceIds
        .map(id => props.resources.find(r => r.id === id))
        .filter((r): r is Resource => !!r)
);

// Auto-manage active resource when selection changes
watch(() => props.selectedResourceIds, (newIds, oldIds) => {
    if (!oldIds || oldIds.length === 0) {
        activeResourceId.value = newIds[0] || null;
        return;
    }
    // New resource added → activate it
    const added = newIds.filter(id => !oldIds.includes(id));
    if (added.length > 0) {
        activeResourceId.value = added[added.length - 1];
        return;
    }
    // Active resource removed → activate first remaining
    if (activeResourceId.value && !newIds.includes(activeResourceId.value)) {
        activeResourceId.value = newIds[0] || null;
    }
}, { immediate: true });

function isResourceConfigured(resourceId: string): boolean {
    const config = props.resourceSlotConfigs.get(resourceId);
    return !!(config?.startTime && config?.endTime);
}

function handleStartTimeUpdate(resourceId: string, time: string | null) {
    const config = props.resourceSlotConfigs.get(resourceId) || {
        startTime: null, endTime: null, customFields: []
    };
    emit('update:resourceSlotConfig', resourceId, { ...config, startTime: time });
}

function handleEndTimeUpdate(resourceId: string, time: string | null) {
    const config = props.resourceSlotConfigs.get(resourceId) || {
        startTime: null, endTime: null, customFields: []
    };
    emit('update:resourceSlotConfig', resourceId, { ...config, endTime: time });
}

function handleCustomFieldsUpdate(resourceId: string, fields: ReservationResourceCustomField[]) {
    const config = props.resourceSlotConfigs.get(resourceId) || {
        startTime: null, endTime: null, customFields: []
    };
    emit('update:resourceSlotConfig', resourceId, { ...config, customFields: fields });
}

function removeResource(resourceId: string) {
    resourceValidationErrors.delete(resourceId);
    emit('remove-resource', resourceId);
}
</script>

<template>
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-0 lg:h-full">

        <!-- Left Panel: Date + Resource Select + Resource List -->
        <div class="lg:col-span-5 lg:border-r lg:border-surface-200 lg:pr-5 lg:overflow-y-auto space-y-4">
            <!-- Date Picker -->
            <div class="flex flex-col gap-2">
                <label for="slot-date" class="font-medium">
                    {{ t('calendar.new.resourcesSlots.addSlot.date') }}
                    <span class="text-red-500">*</span>
                </label>
                <DatePicker id="slot-date" v-model="dateModel"
                    :placeholder="t('calendar.new.resourcesSlots.addSlot.datePlaceholder')" dateFormat="dd/mm/yy"
                    class="w-full" :minDate="minDate" :maxDate="maxDate">
                    <template #inputicon>
                        <i class="fa-regular fa-calendar"></i>
                    </template>
                </DatePicker>
            </div>

            <!-- Resource MultiSelect -->
            <div class="flex flex-col gap-2">
                <label class="font-medium">
                    {{ t('calendar.new.resourcesSlots.selectResources') }}
                    <span class="text-red-500">*</span>
                </label>
                <MultiSelect :modelValue="selectedResourceIds"
                    @update:modelValue="$emit('update:selectedResourceIds', $event)" :options="resources"
                    optionLabel="name" optionValue="id"
                    :placeholder="t('calendar.new.resourcesSlots.addSlot.selectResource')" class="w-full"
                    :loading="resourcesLoading" :disabled="resourcesLoading" filter display="chip"
                    :maxSelectedLabels="3">
                    <template #option="slotProps">
                        <div class="flex items-center gap-3">
                            <div class="w-3 h-3 rounded-full shrink-0"
                                :style="{ backgroundColor: '#' + (slotProps.option.color || '8B5CF6') }"></div>
                            <div class="flex flex-col">
                                <span class="font-medium">{{ slotProps.option.name }}</span>
                                <span class="text-sm text-surface-500">{{ slotProps.option.location }}</span>
                            </div>
                        </div>
                    </template>
                </MultiSelect>
            </div>

            <!-- Selected Resources List -->
            <div v-if="selectedResourceIds.length > 0">
                <p class="text-xs font-semibold text-surface-500 uppercase tracking-wider mb-2">
                    {{ t('calendar.new.preview.resources') }} ({{ selectedResourceIds.length }})
                </p>
                <div class="space-y-1.5 max-h-64 overflow-y-auto">
                    <div v-for="resource in selectedResources" :key="resource.id"
                        class="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border" :class="activeResourceId === resource.id
                            ? 'border-blue-400 bg-blue-50/80 shadow-sm'
                            : 'border-surface-200 hover:border-surface-300 hover:bg-surface-50'"
                        @click="handleResourceClick(resource.id)">
                        <div class="w-3 h-3 rounded-full shrink-0"
                            :style="{ backgroundColor: '#' + (resource.color || '8B5CF6') }">
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="font-medium text-sm truncate">{{
                                resourcesStore.getResourceDisplayNameById(resource.id) || ''
                            }}</p>
                            <p class="text-xs text-surface-500 mt-0.5">
                                {{ resourceSlotConfigs.get(resource.id)?.startTime || '--:--' }}
                                -
                                {{ resourceSlotConfigs.get(resource.id)?.endTime || '--:--' }}
                            </p>
                        </div>
                        <div class="flex items-center gap-1.5">
                            <i v-if="slotCapacities.get(resource.id)?.hasError"
                                class="fa-solid fa-triangle-exclamation text-amber-500 text-xs"></i>
                            <i v-else-if="isResourceConfigured(resource.id)"
                                class="fa-solid fa-circle-check text-green-500 text-xs"></i>
                            <i v-else class="fa-regular fa-circle text-surface-300 text-xs"></i>
                            <button
                                class="w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 text-surface-400 hover:text-red-500 transition-colors"
                                @click.stop="removeResource(resource.id)">
                                <i class="fa-solid fa-xmark text-xs"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Empty state -->
            <div v-else class="text-center py-6 rounded-lg border-2 border-dashed"
                style="background-color: var(--surface-50); border-color: var(--surface-300)">
                <i class="fa-regular fa-calendar-plus text-3xl mb-2 opacity-30"></i>
                <p class="font-medium text-sm">{{ t('calendar.new.resourcesSlots.noSlotsAdded') }}</p>
                <p class="text-xs mt-1 text-surface-500">{{ t('calendar.new.resourcesSlots.addAtLeastOneSlot') }}</p>
            </div>
        </div>

        <!-- Right Panel: Active Resource Configuration (desktop only) -->
        <div class="hidden lg:block lg:col-span-7 lg:pl-5 lg:overflow-y-auto">
            <ResourceSlotCard v-if="activeResource && activeResourceId" :key="activeResourceId"
                :resource="activeResource" :selected-date="selectedDate"
                :start-time="resourceSlotConfigs.get(activeResourceId)?.startTime || null"
                :end-time="resourceSlotConfigs.get(activeResourceId)?.endTime || null"
                :custom-fields="resourceSlotConfigs.get(activeResourceId)?.customFields || []"
                :capacity-info="slotCapacities.get(activeResourceId) || null"
                :editing-reservation-id="editingReservationId"
                @update:start-time="handleStartTimeUpdate(activeResourceId, $event)"
                @update:end-time="handleEndTimeUpdate(activeResourceId, $event)"
                @update:custom-fields="handleCustomFieldsUpdate(activeResourceId, $event)"
                @update:has-validation-error="handleSlotValidationChange(activeResourceId, $event)"
                @remove="removeResource(activeResourceId)" />

            <!-- Empty state when no active resource -->
            <div v-else class="flex flex-col items-center justify-center py-16 text-center h-full">
                <i class="fa-regular fa-hand-pointer text-4xl mb-3 opacity-30"></i>
                <p class="font-medium">{{ t('calendar.new.resourcesSlots.noSlotsAdded') }}</p>
                <p class="text-sm mt-1 text-surface-500">
                    {{ t('calendar.new.resourcesSlots.addAtLeastOneSlot') }}
                </p>
            </div>
        </div>
    </div>

    <!-- Mobile Dialog for Resource Configuration -->
    <Dialog v-model:visible="mobileDialogVisible" modal :draggable="false" :closable="false"
        contentClass="!p-0 overflow-hidden rounded-lg" :pt="{ header: { class: '!p-0' } }"
        :style="{ width: '95vw', maxHeight: '90vh' }" class="lg:hidden">
        <ResourceSlotCard v-if="activeResource && activeResourceId" :key="'mobile-' + activeResourceId"
            :resource="activeResource" :selected-date="selectedDate"
            :start-time="resourceSlotConfigs.get(activeResourceId)?.startTime || null"
            :end-time="resourceSlotConfigs.get(activeResourceId)?.endTime || null"
            :custom-fields="resourceSlotConfigs.get(activeResourceId)?.customFields || []"
            :capacity-info="slotCapacities.get(activeResourceId) || null" :editing-reservation-id="editingReservationId"
            @update:start-time="handleStartTimeUpdate(activeResourceId, $event)"
            @update:end-time="handleEndTimeUpdate(activeResourceId, $event)"
            @update:custom-fields="handleCustomFieldsUpdate(activeResourceId, $event)"
            @update:has-validation-error="handleSlotValidationChange(activeResourceId, $event)"
            @remove="removeResource(activeResourceId); mobileDialogVisible = false" />
        <template #footer>
            <div class="p-3">
                <Button :label="t('common.confirm')" icon="fa-solid fa-check" @click="mobileDialogVisible = false" />
            </div>
        </template>
    </Dialog>
</template>
